var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import NumberPadKeyboard from 'components/keyboards/NumberPadKeyboard.vue';
import AnswerInput from 'components/inputs/AnswerInput.vue';
import HtmlRenderer from 'components/activity/HtmlRenderer.vue';
import ErrorLogger from 'mixins/ErrorLogger';
import { ActivityManagerTyped as ActivityManager } from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import DynamicContent from 'components/activity/DynamicContent.vue';
import RedXSolidIcon from 'components/icons/RedXSolidIcon.vue';
import SuccessCheckSolidIcon from 'components/icons/SuccessCheckSolidIcon.vue';
import { TweenMax } from 'gsap';
import Vue from 'vue';
import ActivityStage from '../components/activity/ActivityStage.vue';
import ActivityWrapper from '../components/activity/ActivityWrapper.vue';
import mixins from 'vue-typed-mixins';
import { EquationBuilderOperator } from 'graph/types/schema-generated-types';
const HINT = 'HINT';
const INCORRECT_ATTEMPT = 'INCORRECT_ATTEMPT';
export default mixins(ActivityManager(), TypingActivityHelper, ErrorLogger).extend({
    name: 'EquationBuilder',
    components: {
        DynamicContent,
        NumberPadKeyboard,
        AnswerInput,
        RedXSolidIcon,
        SuccessCheckSolidIcon,
        HtmlRenderer,
        ActivityStage,
        ActivityWrapper
    },
    data() {
        return {
            answers: {},
            showAnswers: false,
            errors: {},
            disabled: {},
            numOfAttempts: 0,
            showErrors: false,
            equationSymbols: [],
            isComplete: false,
            sounds: new Map(),
            numberOfInputErrors: 0,
            focusedAnswerInputSymbolIndex: undefined,
            numPadAnswerInputOverrides: {}
        };
    },
    mounted() {
        this.enableKeyboard();
    },
    computed: {
        equationAnswers() {
            return this.equationSymbols
                .flatMap(e => (e.fraction ? [...e.fraction.numerator, ...e.fraction.denominator] : [e]))
                .filter(s => s.isAnswer);
        }
    },
    methods: {
        hasIncorrectAnswer() {
            return this.equationAnswers.some(symbol => this.answers[symbol.index] != symbol.characters);
        },
        disableCorrectInputs() {
            this.equationAnswers.forEach(symbol => {
                if (this.answers[symbol.index] == symbol.characters) {
                    Vue.set(this.disabled, symbol.index, true);
                }
            });
        },
        toggleHighlightedErrors() {
            this.showErrors = !this.showErrors;
            this.numberOfInputErrors = 0;
            this.equationAnswers.forEach(symbol => {
                if (this.showErrors && this.answers[symbol.index] != symbol.characters) {
                    this.numberOfInputErrors += 1;
                }
                Vue.set(this.errors, symbol.index, this.showErrors ? this.answers[symbol.index] != symbol.characters : false);
            });
        },
        handleOnActivitySubmit() {
            if (this.isComplete) {
                return;
            }
            this.incrementNumberOfAttempts();
            this.numOfAttempts += 1;
            this.disableCorrectInputs();
            if (Object.keys(this.answers).length === 0 || this.hasIncorrectAnswer()) {
                if (this.numOfAttempts >= 2) {
                    this.showAnswers = true;
                    Object.keys(this.answers).forEach(key => {
                        if (!this.disabled[key]) {
                            Vue.set(this.answers, key, '');
                            Vue.set(this.numPadAnswerInputOverrides, key, '');
                        }
                    });
                    this.promptScreenReaderHintAlert();
                    this.playAudio(HINT);
                }
                else {
                    this.playAudio(INCORRECT_ATTEMPT);
                }
                this.toggleHighlightedErrors();
                this.$nextTick(() => {
                    this.focusFirstEnabledAnswerInput();
                });
                TweenMax.delayedCall(4, () => {
                    this.toggleHighlightedErrors();
                });
                return;
            }
            this.isComplete = true;
            TweenMax.delayedCall(1, () => {
                this.processEndOfActivity();
            });
        },
        focusFirstEnabledAnswerInput() {
            let firstAnswerInput;
            const answerInputs = this.$refs.answerInputs;
            if (Array.isArray(answerInputs)) {
                firstAnswerInput = answerInputs.find(ref => !ref.disabled);
            }
            else {
                firstAnswerInput = answerInputs;
            }
            firstAnswerInput === null || firstAnswerInput === void 0 ? void 0 : firstAnswerInput.focus();
        },
        handleCorrectKey({ character }) {
            if (typeof this.focusedAnswerInputSymbolIndex !== 'undefined')
                this.maybeTriggerNumPadOverride(character);
            this.sndType();
        },
        getActivityState() {
            const { rounds, incorrectAttempt } = this.activityData;
            return {
                instructions: Object.assign(Object.assign({}, rounds[0].instructions), { text: rounds[0].instructions.audio.description }),
                incorrectAttemptVoice: incorrectAttempt.audio,
                hintAttemptVoice: rounds[0].hint.audio
            };
        },
        loadActivity() {
            return __awaiter(this, void 0, void 0, function* () {
                const roundSetup = this.getActivityState();
                this.createRounds(roundSetup);
                const audiosMap = {
                    [HINT]: roundSetup.hintAttemptVoice.src,
                    [INCORRECT_ATTEMPT]: roundSetup.incorrectAttemptVoice.src
                };
                const audioDescriptionsMap = {
                    [HINT]: roundSetup.hintAttemptVoice.description,
                    [INCORRECT_ATTEMPT]: roundSetup.incorrectAttemptVoice.description
                };
                this.sfx = yield this.loadAAAudios(audiosMap);
                this.sfxDescriptions = audioDescriptionsMap;
            });
        },
        startActivity() {
            this.setRound();
            this.$nextTick(this.setActivityContainerHeight);
        },
        transformTermToSymbol(term, index) {
            var _a, _b, _c;
            return {
                index,
                isAnswer: term.placeholder,
                characters: (_c = (_b = (_a = term.value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : (term.operator ? operatorMap[term.operator] : null)) !== null && _c !== void 0 ? _c : ''
            };
        },
        setRound() {
            this.setupNewRound();
            this.enableKeyboard();
            this.setFreeTextTyping(true);
            this.errorLoggerStart();
            let symbolIndex = 0;
            this.equationSymbols = this.currentRound.terms.map(term => {
                const index = symbolIndex;
                let fractionedSymbol;
                if (term.fraction) {
                    fractionedSymbol = {
                        numerator: term.fraction.numerator.map(term => this.transformTermToSymbol(term, symbolIndex++)),
                        denominator: term.fraction.denominator.map(term => this.transformTermToSymbol(term, symbolIndex++))
                    };
                }
                symbolIndex += 1;
                return Object.assign(Object.assign({}, this.transformTermToSymbol(term, index)), { fraction: fractionedSymbol });
            });
            this.$nextTick(() => {
                this.focusFirstEnabledAnswerInput();
            });
        },
        indexOfEquationAnswer(equationSymbolIndex) {
            return this.equationAnswers.findIndex(({ index }) => index === equationSymbolIndex);
        },
        maybeTriggerNumPadOverride(char) {
            setTimeout(() => {
                var _a, _b, _c;
                const lowercaseChar = char.toLowerCase();
                const isNumPadInput = !((_c = (_a = document.activeElement) === null || _a === void 0 ? void 0 : (_b = _a.classList).contains) === null || _c === void 0 ? void 0 : _c.call(_b, 'number-input'));
                const isBackspaceChar = lowercaseChar === 'backspace';
                const isClearChar = lowercaseChar === 'clear';
                const currentAnswerInputVal = this.answers[this.focusedAnswerInputSymbolIndex] || '';
                if (isBackspaceChar) {
                    return;
                }
                if (isNumPadInput && currentAnswerInputVal.length < 3) {
                    Vue.set(this.numPadAnswerInputOverrides, this.focusedAnswerInputSymbolIndex, `${currentAnswerInputVal}${lowercaseChar}`.trim());
                }
                if (isClearChar) {
                    this.triggerNumPadClearOverride();
                }
            });
        },
        triggerNumPadClearOverride() {
            /*
              We have to toggle between undefined and '' so that the
              inputOverride watcher is triggered inside of answer-input
            */
            Vue.set(this.numPadAnswerInputOverrides, this.focusedAnswerInputSymbolIndex, undefined);
            this.$nextTick(() => {
                Vue.set(this.numPadAnswerInputOverrides, this.focusedAnswerInputSymbolIndex, '');
            });
        },
        promptScreenReaderHintAlert() {
            this.equationSymbols.forEach(symbol => {
                if (symbol.fraction) {
                    symbol.fraction.numerator.forEach((numerator, numIndex) => {
                        if (numerator.isAnswer) {
                            this.setScreenReaderAlert(`The correct numerator input for textbox ${numIndex +
                                1} is ${numerator.characters.toString()}.`);
                        }
                    });
                    symbol.fraction.denominator.forEach((denominator, numIndex) => {
                        if (denominator.isAnswer) {
                            this.setScreenReaderAlert(`The correct denominator input for textbox ${numIndex +
                                1} is ${denominator.characters.toString()}.`);
                        }
                    });
                }
                if (symbol.isAnswer) {
                    this.setScreenReaderAlert(`The correct input for textbox ${this.indexOfEquationAnswer(symbol.index) +
                        1} is ${symbol.characters.toString()}.`);
                }
            });
        },
        handleOnAnswerInput(event, index) {
            Vue.set(this.answers, index, event);
            Vue.set(this.numPadAnswerInputOverrides, index, event);
        }
    }
});
const operatorMap = {
    [EquationBuilderOperator.ADDITION]: '+',
    [EquationBuilderOperator.SUBTRACTION]: '-',
    [EquationBuilderOperator.MULTIPLICATION]: 'x',
    [EquationBuilderOperator.DIVISION]: '÷',
    [EquationBuilderOperator.OPEN_PARENTHESES]: '(',
    [EquationBuilderOperator.CLOSE_PARENTHESES]: ')',
    [EquationBuilderOperator.EQUALITY]: '=',
    [EquationBuilderOperator.EXPONENTIATION]: '^',
    [EquationBuilderOperator.MODULUS]: '%'
};
